import React, { useState, useRef, useEffect } from 'react';
import { aiApi } from '../api/client';

const AiAssistant = ({ isOpen, onClose, context, onApplyChange }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Messaggio iniziale quando si apre per la prima volta
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Ciao! Sono il tuo assistente AI per i preventivi. Posso aiutarti a:

• **Riscrivere l'oggetto** del preventivo
• **Modificare i nomi** dei deliverables  
• **Suggerire prezzi** per le voci
• **Migliorare descrizioni** in modo professionale

Dimmi cosa vuoi modificare e ti proporrò le modifiche che potrai applicare con un click.`,
        suggestions: []
      }]);
    }
  }, [isOpen, messages.length]);

  // Parser per estrarre suggerimenti dalla risposta AI
  const parseSuggestions = (content) => {
    const suggestions = [];
    
    // Pattern per trovare suggerimenti nel formato [CAMPO:valore]
    // Esempio: [OGGETTO:Produzione video AI per campagna social media]
    const regex = /\[([A-Z_]+):([^\]]+)\]/g;
    let match;
    
    while ((match = regex.exec(content)) !== null) {
      const field = match[1].toLowerCase();
      const value = match[2].trim();
      
      // Mappa i campi
      const fieldMap = {
        'oggetto': 'oggetto',
        'cliente': 'cliente_nome',
        'cliente_nome': 'cliente_nome',
        'deliverable': 'deliverable',
        'voce': 'deliverable',
        'prezzo': 'prezzo',
        'nota': 'note',
        'note': 'note'
      };
      
      if (fieldMap[field]) {
        suggestions.push({
          field: fieldMap[field],
          value: value,
          originalMatch: match[0]
        });
      }
    }
    
    // Pulisci il contenuto rimuovendo i tag
    let cleanContent = content.replace(/\[([A-Z_]+):([^\]]+)\]/g, '**$2**');
    
    return { cleanContent, suggestions };
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: 'user', content: inputValue };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      // Prepara il contesto completo per l'AI
      const contextWithInstructions = {
        ...context,
        instructions: `IMPORTANTE: Quando suggerisci modifiche, usa SEMPRE questo formato esatto:
[OGGETTO:testo suggerito] per modificare l'oggetto del preventivo
[DELIVERABLE:nome deliverable] per suggerire un nome di deliverable
[PREZZO:numero] per suggerire un prezzo
[NOTE:testo] per suggerire note

Esempio di risposta corretta:
"Ecco la mia proposta per l'oggetto: [OGGETTO:Produzione di 6 contenuti video mediante tecnologia Noema AI, comprensivo di diritti di utilizzo (buyout)]"

Rispondi sempre in italiano, sii conciso e professionale.`
      };

      const response = await aiApi.chat(
        newMessages.filter(m => m.role !== 'system').map(m => ({ 
          role: m.role, 
          content: m.content 
        })),
        contextWithInstructions
      );
      
      const { cleanContent, suggestions } = parseSuggestions(response.data.message);
      
      const assistantMessage = { 
        role: 'assistant', 
        content: cleanContent,
        suggestions: suggestions
      };
      setMessages([...newMessages, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Si è verificato un errore di connessione. Riprova tra qualche secondo.',
        suggestions: []
      };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplySuggestion = (suggestion) => {
    if (onApplyChange) {
      onApplyChange(suggestion.field, suggestion.value);
      
      // Feedback visivo
      setMessages(prev => [...prev, {
        role: 'system',
        content: `✓ Modificato ${suggestion.field === 'oggetto' ? "l'oggetto" : suggestion.field}`,
        suggestions: []
      }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Quick prompts per velocizzare
  const quickPrompts = [
    { label: 'Riscrivi oggetto', prompt: 'Riscrivi l\'oggetto del preventivo in modo più professionale' },
    { label: 'Suggerisci prezzi', prompt: 'Suggerisci dei prezzi per i deliverables attuali' },
    { label: 'Migliora deliverables', prompt: 'Migliora i nomi dei deliverables rendendoli più descrittivi' },
  ];

  const getFieldLabel = (field) => {
    const labels = {
      'oggetto': 'Oggetto',
      'cliente_nome': 'Cliente',
      'deliverable': 'Deliverable',
      'prezzo': 'Prezzo',
      'note': 'Note'
    };
    return labels[field] || field;
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 z-40 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div 
        className={`fixed right-0 top-0 h-full w-[400px] bg-white shadow-2xl transform transition-transform duration-300 ease-out z-50 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-900 to-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-white font-medium text-sm">AI Assistant</h2>
              <p className="text-[10px] text-gray-400">GPT-5.2</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx}>
              {msg.role === 'system' ? (
                <div className="text-center">
                  <span className="text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full">
                    {msg.content}
                  </span>
                </div>
              ) : (
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[90%] ${msg.role === 'user' ? '' : ''}`}>
                    <div 
                      className={`rounded-2xl px-4 py-3 ${
                        msg.role === 'user' 
                          ? 'bg-gray-900 text-white rounded-br-md' 
                          : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap leading-relaxed"
                         dangerouslySetInnerHTML={{ 
                           __html: msg.content
                             .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-black">$1</strong>')
                             .replace(/\n/g, '<br/>') 
                         }} 
                      />
                    </div>
                    
                    {/* Suggestion buttons */}
                    {msg.suggestions && msg.suggestions.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {msg.suggestions.map((suggestion, sIdx) => (
                          <button
                            key={sIdx}
                            onClick={() => handleApplySuggestion(suggestion)}
                            className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-gray-900 hover:shadow-md transition-all group text-left"
                          >
                            <div className="flex-1 min-w-0">
                              <span className="text-[10px] uppercase tracking-wider text-gray-400 block mb-1">
                                {getFieldLabel(suggestion.field)}
                              </span>
                              <span className="text-sm text-gray-800 block truncate">
                                {suggestion.field === 'prezzo' ? `€ ${suggestion.value}` : suggestion.value}
                              </span>
                            </div>
                            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 group-hover:bg-gray-900 rounded-lg flex items-center justify-center transition-colors">
                              <svg className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts */}
        {messages.length <= 1 && (
          <div className="px-4 pb-2">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {quickPrompts.map((qp, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setInputValue(qp.prompt);
                    inputRef.current?.focus();
                  }}
                  className="flex-shrink-0 px-3 py-2 text-xs bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full transition-colors whitespace-nowrap"
                >
                  {qp.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Cosa vuoi modificare?"
              className="flex-1 resize-none bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-200 transition-all"
              rows="1"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="w-11 h-11 bg-gray-900 text-white rounded-xl flex items-center justify-center hover:bg-black disabled:opacity-30 disabled:cursor-not-allowed transition-all flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AiAssistant;
